import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import {
  LayoutDashboard,
  Boxes,
  AlertTriangle,
  ListChecks,
  CloudUpload,
  FileSpreadsheet,
  FileText,
  Waypoints,
  ClipboardCheck,
  Inbox,
  Menu,
  LogOut,
  WifiOff,
  X,
} from 'lucide-react';
import { useSession } from './session';
import { subscribeSync, flush, type SyncState } from '@/data/sync';
import { APP_VERSION } from '@/data/supabase';
import { useIsFetching } from '@tanstack/react-query';
import { AlertBar, IconButton, LoadBar, Select, useIsMobile } from '@/components/ui';

/** Agrupamento curto e explícito (skill §11): operação primeiro, dados depois. */
const NAV_GROUPS = [
  {
    label: 'Operação',
    items: [
      { to: '/dashboard', label: 'Visão geral', icon: LayoutDashboard },
      { to: '/ativos', label: 'Ativos', icon: Boxes },
      { to: '/inspecoes', label: 'Inspeção em campo', icon: ClipboardCheck },
      { to: '/propostas', label: 'Propostas de NC', icon: Inbox },
      { to: '/nao-conformidades', label: 'Não conformidades', icon: AlertTriangle },
      { to: '/acoes', label: 'Plano de ações', icon: ListChecks },
      { to: '/jornada', label: 'Jornada de Conformidade', icon: Waypoints },
      { to: '/documentos', label: 'Documentos', icon: FileText },
    ],
  },
  {
    label: 'Dados',
    items: [{ to: '/importar', label: 'Importar planilha', icon: FileSpreadsheet }],
  },
];

function SyncIndicator() {
  const [{ state, pending }, set] = useState<{ state: SyncState; pending: number }>({
    state: 'idle',
    pending: 0,
  });
  useEffect(() => subscribeSync((s) => set({ state: s.state, pending: s.pending })), []);

  if (pending === 0 && state !== 'syncing') return null;

  return (
    <NavLink to="/fila" className="save-feedback" title="Fila de sincronização" onClick={() => void flush()}>
      {state === 'syncing' ? (
        <>
          <span className="spinner" aria-hidden /> Sincronizando…
        </>
      ) : (
        <span className={pending ? 'save-feedback--pending' : ''}>
          <CloudUpload size={14} aria-hidden /> {pending} pendente{pending === 1 ? '' : 's'}
        </span>
      )}
    </NavLink>
  );
}

/**
 * Estado da rede como faixa fina no topo do conteúdo (skill §14), não como
 * cartão. Interessa a quem está no chão de fábrica: o trabalho continua, e a
 * faixa diz isso em vez de deixar a pessoa achar que perdeu o que digitou.
 */
function OfflineBar() {
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && !navigator.onLine);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  if (!offline) return null;
  return (
    <AlertBar kind="warn" icon={<WifiOff size={14} aria-hidden />}>
      Sem conexão. O que você preencher fica salvo no aparelho e sincroniza sozinho quando o sinal
      voltar.
    </AlertBar>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const { memberships, orgId, setOrgId, org, signOut } = useSession();
  const [drawer, setDrawer] = useState(false);
  const location = useLocation();

  useEffect(() => setDrawer(false), [location.pathname]);

  // Esc fecha a gaveta. Quem abriu um menu sobreposto espera sair dele pelo
  // teclado; sem isso, só o toque no fundo ou no X resolvia.
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawer(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawer]);

  // reativo: volta ao layout desktop ao girar o celular ou redimensionar
  const isMobile = useIsMobile();

  // Um indicador de carregamento para a aplicação inteira, em vez de um
  // spinner por consulta: a tela para de tremer a cada busca.
  const fetching = useIsFetching();

  const sidebarClass = [
    'shell__sidebar',
    isMobile ? 'shell__sidebar--drawer' : '',
    isMobile && drawer ? 'shell__sidebar--open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`shell${isMobile ? ' shell--mobile' : ''}`}>
      <aside className={sidebarClass}>
        <div className="brand">
          <span className="brand__mark" aria-hidden>
            NR
          </span>
          <span className="brand__name truncate">Gestão NR-12 / NR-10</span>
          {isMobile && (
            <>
              <span className="brand__spacer" />
              <IconButton
                label="Fechar menu"
                className="icon-btn--on-dark"
                onClick={() => setDrawer(false)}
              >
                <X size={18} />
              </IconButton>
            </>
          )}
        </div>

        <nav className="nav" aria-label="Navegação principal">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="nav__group-label">{group.label}</div>
              {group.items.map(({ to, label, icon: Icon }) => (
                <NavLink key={to} to={to} className="nav__item">
                  <Icon size={16} aria-hidden />
                  {label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar__foot">
          <Select
            value={orgId ?? ''}
            onChange={(e) => setOrgId(e.target.value)}
            aria-label="Organização ativa"
            className="select--on-dark"
          >
            {memberships.map((m) => (
              <option key={m.organization_id} value={m.organization_id}>
                {m.organization_name}
              </option>
            ))}
          </Select>
          <div className="sidebar__meta">
            <span className="truncate">{org?.role_name}</span>
            <span aria-hidden>·</span>
            <span className="mono">build {APP_VERSION.slice(0, 10)}</span>
          </div>
        </div>
      </aside>

      {isMobile && drawer && (
        <button
          type="button"
          className="shell__scrim"
          aria-label="Fechar menu"
          onClick={() => setDrawer(false)}
        />
      )}

      <header className="shell__topbar">
        {isMobile && (
          <IconButton label="Abrir menu" onClick={() => setDrawer(true)}>
            <Menu size={20} />
          </IconButton>
        )}
        <strong className="shell__topbar-title truncate">{org?.organization_name ?? '—'}</strong>
        <span className="toolbar__spacer" />
        <SyncIndicator />
        <IconButton label="Sair" onClick={() => void signOut()}>
          <LogOut size={16} />
        </IconButton>
      </header>

      <main className="shell__main">
        <LoadBar active={fetching > 0} />
        <OfflineBar />
        {children}
      </main>
    </div>
  );
}
